import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Minus, X, Package, AlertTriangle, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { getProducts, getProductBranchStock } from '../../api/products';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const ProductSelector = ({ onProductsChange, selectedProducts, isMultiBranch = false, branches = [] }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';
  const canSelectBranches = isMultiBranch && ['super_admin', 'system_admin', 'retail_admin'].includes(user?.role);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showContactAdminAlert, setShowContactAdminAlert] = useState(false);
  const [alertProduct, setAlertProduct] = useState(null);
  const [expandedBranchIndex, setExpandedBranchIndex] = useState(null); // Which product's branch section is expanded
  const [loadingBranchStock, setLoadingBranchStock] = useState(null); // Product ID that's loading
  const dropdownRef = useRef(null);

  // Search products
  useEffect(() => {
    const searchProducts = async () => {
      if (searchTerm.length < 2) {
        setProducts([]);
        return;
      }

      setLoading(true);
      try {
        const response = await getProducts({
          search: searchTerm,
          limit: 10,
          isActive: true,
          inStock: true
        });
        setProducts(response.products || []);
        setShowDropdown(true);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addProduct = async (product) => {
    // Check if product already in cart
    const existing = selectedProducts.find(p => p.product_id === product.id);

    if (existing) {
      toast.info('Product already in cart. Adjust quantity below.');
      return;
    }

    // Check stock availability
    if (product.stock_quantity < 1) {
      toast.error('Product is out of stock');
      return;
    }

    let branchStock = [];
    
    // For multi-branch mode, fetch branch stock breakdown
    if (canSelectBranches) {
      try {
        setLoadingBranchStock(product.id);
        const response = await getProductBranchStock(product.id);
        branchStock = response.data?.branches || [];
      } catch (error) {
        console.error('Failed to fetch branch stock:', error);
        // Fall back to empty branch stock
      } finally {
        setLoadingBranchStock(null);
      }
    }

    const newProduct = {
      product_id: product.id,
      product_code: product.product_code,
      product_name: product.name,
      product_type: product.product_type || 'tile',
      cost_price: parseFloat(product.cost_price),
      selling_price: parseFloat(product.selling_price),
      original_selling_price: parseFloat(product.selling_price),
      quantity: 0,
      stock_available: product.stock_quantity,
      unit_discount: 0,
      unit: product.unit || 'cartons',
      // Multi-branch data
      branchStock: branchStock, // Array of { branchId, branchName, stockQuantity }
      branchAllocations: canSelectBranches ? [] : null // Array of { branchId, quantity } - only for multi-branch mode
    };

    // Add new product at the beginning of the list (top)
    onProductsChange([newProduct, ...selectedProducts]);
    setSearchTerm('');
    setShowDropdown(false);
    
    // If multi-branch, expand the branch selection for this product
    if (canSelectBranches && branchStock.length > 0) {
      setExpandedBranchIndex(0); // First item since we add to beginning
    }
    
    toast.success(
      <span><span className="text-amber-500 font-bold">{product.product_code}</span> Added to Items</span>,
      { icon: '🛒' }
    );
  };

  const updateQuantity = (index, quantity) => {
    const updated = [...selectedProducts];
    const product = updated[index];

    // Allow 0 quantity (will be validated on checkout)
    if (quantity < 0) {
      return;
    }

    if (quantity > product.stock_available) {
      if (isAdmin) {
        toast.error(`Only ${product.stock_available} units available in stock`);
      } else {
        // For staff: show alert to contact admin
        setAlertProduct({ name: product.product_name, code: product.product_code, requested: quantity });
        setShowContactAdminAlert(true);
      }
      return;
    }

    updated[index] = { ...product, quantity };
    onProductsChange(updated);
  };

  // Update quantity for a specific branch (multi-branch mode)
  const updateBranchAllocation = (productIndex, branchId, quantity) => {
    const updated = [...selectedProducts];
    const product = updated[productIndex];
    
    const newQty = parseInt(quantity) || 0;
    if (newQty < 0) return;
    
    // Check branch stock limit
    const branchInfo = product.branchStock?.find(b => b.branchId === branchId);
    if (branchInfo && newQty > branchInfo.stockQuantity) {
      toast.error(`Only ${branchInfo.stockQuantity} available in ${branchInfo.branchName}`);
      return;
    }
    
    // Update or add the allocation
    let allocations = [...(product.branchAllocations || [])];
    const existingIdx = allocations.findIndex(a => a.branchId === branchId);
    
    if (existingIdx >= 0) {
      if (newQty === 0) {
        allocations.splice(existingIdx, 1);
      } else {
        allocations[existingIdx] = { branchId, quantity: newQty };
      }
    } else if (newQty > 0) {
      allocations.push({ branchId, quantity: newQty });
    }
    
    // Calculate total quantity as sum of all branch allocations
    const totalQuantity = allocations.reduce((sum, a) => sum + a.quantity, 0);
    
    updated[productIndex] = { 
      ...product, 
      branchAllocations: allocations,
      quantity: totalQuantity 
    };
    onProductsChange(updated);
  };

  // Get the allocated quantity for a specific branch
  const getBranchQuantity = (product, branchId) => {
    const alloc = product.branchAllocations?.find(a => a.branchId === branchId);
    return alloc ? alloc.quantity : 0;
  };

  const updateDiscount = (index, discount) => {
    const updated = [...selectedProducts];
    const product = updated[index];

    if (discount < 0) {
      toast.error('Discount cannot be negative');
      return;
    }

    if (discount >= product.selling_price) {
      toast.error('Discount must be less than selling price');
      return;
    }

    updated[index] = { ...product, unit_discount: parseFloat(discount) || 0 };
    onProductsChange(updated);
  };

  const updateSoldPrice = (index, price) => {
    const updated = [...selectedProducts];
    const product = updated[index];

    const newPrice = parseFloat(price) || 0;
    if (newPrice < 0) {
      toast.error('Price cannot be negative');
      return;
    }

    updated[index] = { ...product, selling_price: newPrice };
    onProductsChange(updated);
  };

  const removeProduct = (index) => {
    const product = selectedProducts[index];
    const updated = selectedProducts.filter((_, i) => i !== index);
    onProductsChange(updated);
    toast.info(
      <span><span className="text-amber-500 font-bold">{product.product_code}</span> Removed from Items</span>,
      { icon: '🗑️' }
    );
  };

  const calculateLineTotal = (product) => {
    return ((product.selling_price - product.unit_discount) * product.quantity).toFixed(2);
  };

  const calculateLineProfit = (product) => {
    return (((product.selling_price - product.unit_discount) - product.cost_price) * product.quantity).toFixed(2);
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-gray w-5 h-5" />
          <input
            type="text"
            placeholder="Search products by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchTerm && setShowDropdown(true)}
            className="w-full pl-10 pr-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold focus:border-transparent"
          />
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-secondary-lightGray rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-secondary-gray">Searching...</div>
            ) : products.length > 0 ? (
              <div>
                {products.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => addProduct(product)}
                    className="w-full px-4 py-3 text-left bg-white hover:bg-secondary-offWhite transition-colors border-b border-secondary-lightGray last:border-b-0 cursor-pointer"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-primary-black">{product.name}</p>
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${product.product_type === 'accessory' ? 'bg-purple-100 text-purple-700 border border-purple-300' : 'bg-blue-100 text-blue-700 border border-blue-300'}`}>
                            {product.product_type === 'accessory' ? 'ACC' : 'TILE'}
                          </span>
                        </div>
                        <p className="text-sm text-secondary-gray">{product.product_code}</p>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <p className="font-semibold text-accent-gold whitespace-nowrap">₦{parseFloat(product.selling_price).toLocaleString()}</p>
                        {isAdmin && (
                          <p className="text-xs text-secondary-gray whitespace-nowrap">Stock: {product.stock_quantity} {product.unit || 'cartons'}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : searchTerm.length >= 2 ? (
              <div className="p-4 text-center text-secondary-gray">No products found</div>
            ) : (
              <div className="p-4 text-center text-secondary-gray">Type to search products</div>
            )}
          </div>
        )}
      </div>

      {/* Selected Products */}
      {selectedProducts.length > 0 && (
        <div className="border-2 border-accent-gold/30 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-primary-black text-white px-4 py-3 font-semibold flex items-center justify-between">
            <span>🛒 Items ({selectedProducts.length})</span>
            {selectedProducts.length > 2 && (
              <span className="text-xs text-gray-400">Scroll for more ↓</span>
            )}
          </div>

          <div className={`p-3 space-y-4 bg-gray-50 ${selectedProducts.length > 2 ? 'max-h-[420px] overflow-y-auto' : ''}`}>
            {selectedProducts.map((product, index) => (
              <div key={index} className="relative p-4 bg-white border-2 border-accent-gold/20 rounded-xl shadow-md hover:shadow-lg hover:border-accent-gold/50 transition-all">
                {/* Product Number Badge */}
                <div className="absolute -top-2 -left-2 w-7 h-7 bg-accent-gold rounded-full flex items-center justify-center text-sm font-bold text-primary-black shadow-md z-10">
                  {index + 1}
                </div>
                
                <div className="flex justify-between items-start mb-3 pl-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-primary-black">{product.product_name}</h4>
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${product.product_type === 'accessory' ? 'bg-purple-100 text-purple-700 border border-purple-300' : 'bg-blue-100 text-blue-700 border border-blue-300'}`}>
                        {product.product_type === 'accessory' ? 'ACC' : 'TILE'}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-accent-gold">{product.product_code}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeProduct(index)}
                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors flex items-center justify-center"
                    title="Remove product"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {/* Quantity */}
                  <div>
                    <label className="block text-xs text-secondary-gray mb-1">
                      Quantity
                      {canSelectBranches && product.branchStock?.length > 0 && (
                        <span className="ml-1 text-blue-600">(via branches)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={product.quantity === '' ? '' : product.quantity}
                      onChange={(e) => {
                        // If branch allocations are required, don't allow direct quantity edit
                        if (canSelectBranches && product.branchStock?.length > 0) {
                          return;
                        }
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value === '') {
                          updateQuantity(index, 0);
                        } else {
                          updateQuantity(index, parseInt(value));
                        }
                      }}
                      onFocus={(e) => {
                        // If multi-branch, expand branch section instead
                        if (canSelectBranches && product.branchStock?.length > 0) {
                          setExpandedBranchIndex(index);
                          return;
                        }
                        e.target.select();
                      }}
                      placeholder="0"
                      readOnly={canSelectBranches && product.branchStock?.length > 0}
                      className={`w-full px-3 py-2 border rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                        canSelectBranches && product.branchStock?.length > 0 
                          ? 'bg-blue-50 border-blue-300 cursor-pointer' 
                          : 'border-secondary-lightGray'
                      }`}
                    />
                    {isAdmin && !(canSelectBranches && product.branchStock?.length > 0) && (
                      <p className="text-xs text-secondary-gray mt-1">Max: {product.stock_available}</p>
                    )}
                    {canSelectBranches && product.branchStock?.length > 0 && (
                      <p className="text-xs text-blue-600 mt-1">Click to allocate →</p>
                    )}
                  </div>

                  {/* Sold Price - Editable */}
                  <div>
                    <label className="block text-xs text-secondary-gray mb-1">Sold Price</label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-secondary-gray text-sm">₦</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={product.selling_price}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          updateSoldPrice(index, value);
                        }}
                        className={`w-full pl-6 pr-2 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                          product.selling_price < product.cost_price 
                            ? 'border-red-500 bg-red-50' 
                            : product.selling_price !== product.original_selling_price
                              ? 'border-yellow-400 bg-yellow-50'
                              : 'border-secondary-lightGray'
                        }`}
                        placeholder="0.00"
                      />
                    </div>
                    {product.selling_price < product.cost_price && (
                      <p className="text-xs text-red-500 mt-1">⚠️ Below cost!</p>
                    )}
                    {product.selling_price !== product.original_selling_price && product.selling_price >= product.cost_price && (
                      <p className="text-xs text-yellow-600 mt-1">Modified</p>
                    )}
                  </div>

                  {/* Discount */}
                  <div>
                    <label className="block text-xs text-secondary-gray mb-1">Discount/Unit</label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-secondary-gray text-sm">₦</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={product.unit_discount}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          updateDiscount(index, value);
                        }}
                        className="w-full pl-6 pr-2 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold bg-white"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Line Total */}
                      <div className="text-right">
                        <p className="font-medium text-primary-black">₦{((product.selling_price - (product.unit_discount || 0)) * product.quantity).toLocaleString()}</p>
                        {isAdmin && (
                          <p className={`text-xs ${calculateLineProfit(product) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {calculateLineProfit(product) >= 0 ? 'Profit: ' : 'Loss: '}
                            ₦{Math.abs(parseFloat(calculateLineProfit(product))).toLocaleString()}
                          </p>
                        )}
                      </div>
                </div>

                {/* Branch Stock Allocation (Multi-branch mode) */}
                {canSelectBranches && product.branchStock && product.branchStock.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setExpandedBranchIndex(expandedBranchIndex === index ? null : index)}
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Building2 className="w-4 h-4" />
                      <span>Branch Stock Allocation</span>
                      {expandedBranchIndex === index ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      {product.branchAllocations?.length > 0 && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {product.branchAllocations.length} branches
                        </span>
                      )}
                    </button>
                    
                    {expandedBranchIndex === index && (
                      <div className="mt-3 space-y-2 bg-blue-50/50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2">Select quantities from available branches:</p>
                        {product.branchStock.map((branch) => (
                          <div key={branch.branchId} className="flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-200">
                            <div className="flex-1">
                              <span className="text-sm font-medium text-gray-800">
                                {branch.branchName}
                                {branch.isMain && <span className="ml-1 text-xs text-amber-600">(Main)</span>}
                              </span>
                              <span className="text-xs text-gray-500 ml-2">
                                Stock: {branch.stockQuantity}
                              </span>
                            </div>
                            <input
                              type="number"
                              min="0"
                              max={branch.stockQuantity}
                              value={getBranchQuantity(product, branch.branchId)}
                              onChange={(e) => updateBranchAllocation(index, branch.branchId, e.target.value)}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                              placeholder="0"
                            />
                          </div>
                        ))}
                        <div className="flex justify-between items-center pt-2 border-t border-blue-200 mt-2">
                          <span className="text-xs text-gray-600">Total from branches:</span>
                          <span className="text-sm font-bold text-blue-700">{product.quantity} {product.unit}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contact Admin Alert Modal for Staff */}
      {showContactAdminAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-orange-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-primary-black">Low Stock Product</h3>
            </div>
            <p className="text-secondary-gray mb-4">
              The quantity you entered for <strong className="text-accent-gold">{alertProduct?.code}</strong> exceeds what is currently available in stock.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-orange-800">
                <strong>Please contact an administrator</strong> to check current stock levels and complete this sale.
              </p>
            </div>
            <button
              onClick={() => setShowContactAdminAlert(false)}
              className="w-full px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-gold/90 transition-colors"
            >
              Understood
            </button>
          </div>
        </div>
      )}

      {selectedProducts.length === 0 && (
        <div className="text-center py-8 text-secondary-gray">
          <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No products added yet</p>
          <p className="text-sm">Search and add products to create a sale</p>
        </div>
      )}
    </div>
  );
};

export default ProductSelector;
